import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import Money from "../../components/Money";
import { useHouseState, useHouseDispatch } from "../../contexts/HouseContext";
import { getPlayerSummaryStats } from "../../services/ledgerService";
import { PlayerSearch } from "../ledger/components/PlayerSearch";
import { computeMemberStats, initialsOf } from "./memberStats";
import { AddPlayerModal } from "./AddPlayerModal";

import "../ledger/Ledger.css";
import "./Players.css";

const SORT_BY = {
    NAME: "name",
    GAMES_PLAYED: "gamesPlayed",
    NET: "allTimeNet",
    HIGHEST_WIN: "highestWin",
};

const SORT_ORDER = { ASC: "asc", DESC: "desc" };

const SORT_CHIPS = [
    { key: SORT_BY.NAME, label: "Name" },
    { key: SORT_BY.GAMES_PLAYED, label: "Sessions" },
    { key: SORT_BY.NET, label: "All-time net" },
    { key: SORT_BY.HIGHEST_WIN, label: "Best night" },
];

/**
 * Players index — the host's roster, re-skinned per the v1.6 redesign
 * (`plans/dashboard-redesign/claude-design-source/project/v15-player.jsx`
 * is the detail; this index shares its visual language). Each row carries
 * the player's all-time book and links through to the head-to-head detail.
 *
 * A row of summary stat tiles (fed by GET /api/players/summary) sits atop
 * the list; a search bar filters the roster by name with the same logic as
 * the in-game player filter; the "Add player" button opens the roster modal.
 */
export default function Members() {
    const { house, games } = useHouseState();
    const { loadGames } = useHouseDispatch();
    const navigate = useNavigate();
    const [sortConfig, setSortConfig] = useState({
        sortBy: SORT_BY.NAME,
        sortOrder: SORT_ORDER.ASC,
    });
    const [query, setQuery] = useState("");
    const [showAdd, setShowAdd] = useState(false);
    const [summary, setSummary] = useState(null);

    const refreshSummary = useCallback(async () => {
        try {
            setSummary(await getPlayerSummaryStats());
        } catch (e) {
            console.error("Failed to load player summary stats:", e);
        }
    }, []);

    useEffect(() => {
        const gameIds = Object.keys(games);
        if (gameIds.length) loadGames(gameIds);
        refreshSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!house) {
        return <div className="players-loading">Loading…</div>;
    }

    const members = (house.members || []).filter((m) => m && m.id);

    const withStats = members.map((member) => ({
        member,
        stats: computeMemberStats(member.id, games),
    }));

    const handleSort = (sortBy) => {
        setSortConfig((prev) => ({
            sortBy,
            sortOrder:
                (prev.sortBy === sortBy && prev.sortOrder === SORT_ORDER.DESC) ||
                (prev.sortBy !== sortBy && sortBy === SORT_BY.NAME)
                    ? SORT_ORDER.ASC
                    : SORT_ORDER.DESC,
        }));
    };

    const sorted = [...withStats].sort((a, b) => {
        const mult = sortConfig.sortOrder === SORT_ORDER.ASC ? 1 : -1;
        switch (sortConfig.sortBy) {
            case SORT_BY.NAME:
                return a.member.name.localeCompare(b.member.name) * mult;
            case SORT_BY.GAMES_PLAYED:
                return (a.stats.gamesPlayed - b.stats.gamesPlayed) * mult;
            case SORT_BY.NET:
                return (a.stats.allTimeNet - b.stats.allTimeNet) * mult;
            case SORT_BY.HIGHEST_WIN:
                return (a.stats.highestWin - b.stats.highestWin) * mult;
            default:
                return 0;
        }
    });

    // Same filter logic as the in-game player search: case-insensitive
    // substring match on the player's name.
    const needle = query.trim().toLowerCase();
    const visible = needle
        ? sorted.filter(({ member }) => member.name.toLowerCase().includes(needle))
        : sorted;

    return (
        <main className="players">
            <header className="players-mast">
                <div>
                    <div className="players-mast-kicker">
                        Roster · {members.length} {members.length === 1 ? "regular" : "regulars"}
                    </div>
                    <h1>
                        The <em>players.</em>
                    </h1>
                </div>
                <div className="players-mast-actions">
                    {members.length > 0 && (
                        <div className="players-search">
                            <PlayerSearch value={query} setTypeaheadInput={setQuery} />
                        </div>
                    )}
                    <button
                        type="button"
                        className="players-add-btn"
                        onClick={() => setShowAdd(true)}
                    >
                        + Add player
                    </button>
                </div>
            </header>

            <SummaryTiles summary={summary} memberCount={members.length} />

            {members.length === 0 ? (
                <div className="players-empty">No players yet — add your first regular.</div>
            ) : (
                <>
                    <div className="players-sort">
                        {SORT_CHIPS.map((chip) => {
                            const active = sortConfig.sortBy === chip.key;
                            return (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className={`players-chip${active ? " is-active" : ""}`}
                                    onClick={() => handleSort(chip.key)}
                                >
                                    {chip.label}
                                    {active && (
                                        <span className="players-chip-dir">
                                            {sortConfig.sortOrder === SORT_ORDER.ASC ? "↑" : "↓"}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="players-head">
                        <span>Player</span>
                        <span className="col-num">Sessions</span>
                        <span className="col-num">Win rate</span>
                        <span className="col-money">Best night</span>
                        <span className="col-money">All-time</span>
                    </div>

                    {visible.length === 0 ? (
                        <div className="players-empty players-empty-filter">
                            No players match “{query.trim()}”.
                        </div>
                    ) : (
                        visible.map(({ member, stats }) => (
                            <PlayerRow
                                key={member.id}
                                member={member}
                                stats={stats}
                                onOpen={() => navigate(`/players/${member.id}`)}
                            />
                        ))
                    )}
                </>
            )}

            <AddPlayerModal
                show={showAdd}
                handleClose={() => setShowAdd(false)}
                onAdded={refreshSummary}
            />
        </main>
    );
}

const SummaryTiles = ({ summary, memberCount }) => {
    const loading = summary === null;
    const earner = summary?.topEarner;

    const tiles = [
        {
            label: "Players",
            value: loading ? "—" : summary.totalPlayers ?? memberCount,
            trend: loading ? "" : `${summary.activePlayers} active`,
        },
        {
            label: "Top earner",
            value:
                loading || !earner ? "—" : <Money amount={earner.net} signed={earner.net !== 0} />,
            valueClass: earner && earner.net > 0 ? "is-up" : earner && earner.net < 0 ? "is-down" : "",
            trend: loading ? "" : earner ? earner.name : "no games yet",
        },
        {
            label: "Sessions",
            value: loading ? "—" : summary.totalGames,
            trend: loading ? "" : "logged",
        },
        {
            label: "Unsettled",
            value: loading ? "—" : <Money amount={summary.unsettledAmount} />,
            valueClass: !loading && summary.unsettledAmount > 0 ? "is-gold" : "",
            trend: loading
                ? ""
                : summary.unsettledPlayers > 0
                  ? `${summary.unsettledPlayers} ${summary.unsettledPlayers === 1 ? "player" : "players"}`
                  : "all settled",
            trendClass: !loading && summary.unsettledPlayers > 0 ? "is-down" : "",
        },
    ];

    return (
        <div className="players-stats">
            {tiles.map((t) => (
                <div className="players-stat" key={t.label}>
                    <div className="players-stat-label">{t.label}</div>
                    <div className={`players-stat-fig ${t.valueClass || ""}`}>{t.value}</div>
                    <div className={`players-stat-trend ${t.trendClass || ""}`}>{t.trend || " "}</div>
                </div>
            ))}
        </div>
    );
};

const PlayerRow = ({ member, stats, onOpen }) => {
    const netClass =
        stats.allTimeNet > 0 ? "is-up" : stats.allTimeNet < 0 ? "is-down" : "is-zero";

    return (
        <div
            className="players-row"
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen();
                }
            }}
        >
            <div className="players-row-id">
                <span className="players-avatar" aria-hidden="true">
                    {initialsOf(member.name)}
                </span>
                <div className="players-row-name">
                    <div className="players-row-title">{member.name}</div>
                    <div className="players-row-meta">
                        {stats.lastPlayed
                            ? `last played ${lastPlayedLabel(stats.lastPlayed.date)}`
                            : "no sessions yet"}
                        {stats.owed > 0 && (
                            <span className="players-row-owed"> · ◔ owes {fmtAbs(stats.owed)}</span>
                        )}
                    </div>
                </div>
            </div>
            <div className="players-row-num">{stats.gamesPlayed}</div>
            <div className="players-row-num">
                {stats.gamesPlayed ? `${Math.round(stats.winRate * 100)}%` : "—"}
            </div>
            <div className="players-row-money">
                {stats.highestWin > 0 ? <Money amount={stats.highestWin} signed /> : "—"}
            </div>
            <div className={`players-row-money players-row-net ${netClass}`}>
                <Money amount={stats.allTimeNet} signed={stats.allTimeNet !== 0} />
            </div>
        </div>
    );
};

function fmtAbs(amount) {
    return `$${Math.abs(amount).toLocaleString("en-US")}`;
}

function lastPlayedLabel(date) {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "—";
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 14) return `${days} days ago`;
    if (days < 60) return `${Math.round(days / 7)} wks ago`;
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}
